// FileTransferProgress — 文件传输进度
// 三种状态：
//   1. 接收方收到文件清单（pendingIncoming）→ 展示接受/拒绝 UI
//   2. 传输中 → 逐文件进度条
//   3. 完成/取消/拒绝 → 状态汇总

import { useState } from 'react';
import type { FileDto, TransferStatus } from '@kuaichuan/shared';
import type { TransferFile } from '../../store/sessionStore';

interface FileTransferProgressProps {
  files: TransferFile[];
  status: TransferStatus;
  role: 'sender' | 'receiver' | null;
  remoteAlias: string | null;
  pendingIncoming: FileDto[] | null;
  onCancel: () => void;
  onAccept: (selectedIds: string[]) => void;
  onDecline: () => void;
}

const STATUS_LABEL: Record<TransferStatus, string> = {
  idle: '空闲',
  connecting: '连接建立中…',
  sdpExchanged: '协商中…',
  connected: '已连接',
  transferring: '传输中…',
  done: '已完成',
  declined: '已拒绝',
  canceled: '已取消',
  error: '出错',
};

export function FileTransferProgress({
  files,
  status,
  role,
  remoteAlias,
  pendingIncoming,
  onCancel,
  onAccept,
  onDecline,
}: FileTransferProgressProps) {
  // 接收方：等待用户确认接受文件
  const isAwaitingAccept = role === 'receiver' && pendingIncoming && pendingIncoming.length > 0;

  if (isAwaitingAccept) {
    return (
      <AcceptSheet
        files={pendingIncoming!}
        remoteAlias={remoteAlias}
        onAccept={onAccept}
        onDecline={onDecline}
      />
    );
  }

  const transferring = status === 'transferring' || status === 'connected';
  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const doneBytes = files.reduce((s, f) => s + Math.min(f.receivedBytes, f.size), 0);
  const overallPct = totalBytes > 0 ? Math.round((doneBytes / totalBytes) * 100) : 0;

  return (
    <div className="fixed inset-x-0 bottom-0 md:bottom-4 md:left-1/2 md:-translate-x-1/2 z-40 md:z-30 md:w-[min(640px,92vw)]">
      <div className="card mx-0 md:mx-auto rounded-b-none md:rounded-lg shadow-pop overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/60 bg-surface">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl shrink-0">{role === 'sender' ? '📤' : '📥'}</span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-ink truncate">
                {role === 'sender' ? `发送给 ${remoteAlias ?? '设备'}` : `接收自 ${remoteAlias ?? '设备'}`}
              </div>
              <div className="text-xs text-muted">{STATUS_LABEL[status]}</div>
            </div>
          </div>
          {transferring && (
            <button type="button" onClick={onCancel} className="text-xs text-danger hover:underline shrink-0">
              取消
            </button>
          )}
        </div>

        {/* 总进度 */}
        {transferring && totalBytes > 0 && (
          <div className="px-5 py-2.5 border-b border-border/60">
            <div className="flex justify-between text-xs text-muted mb-1">
              <span>总进度</span>
              <span>{overallPct}% · {formatBytes(doneBytes)} / {formatBytes(totalBytes)}</span>
            </div>
            <div className="h-2 rounded-full bg-bg overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${overallPct}%` }}
              />
            </div>
          </div>
        )}

        {/* 文件列表 */}
        <ul className="max-h-56 overflow-y-auto divide-y divide-border/40">
          {files.map((f) => (
            <FileRow key={f.id} file={f} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function FileRow({ file }: { file: TransferFile }) {
  const pct = file.size > 0 ? Math.round((Math.min(file.receivedBytes, file.size) / file.size) * 100) : 0;
  const icon = fileEmoji(file.fileType);
  const statusNode = (() => {
    switch (file.status) {
      case 'pending':
        return <span className="text-muted">等待中</span>;
      case 'sending':
        return <span className="text-primary">{pct}%</span>;
      case 'done':
        return <span className="text-success">✓ 完成</span>;
      case 'error':
        return <span className="text-danger" title={file.error}>✗ 失败</span>;
      default:
        return null;
    }
  })();

  return (
    <li className="flex items-center gap-3 px-5 py-2.5">
      <span className="text-xl shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink truncate" title={file.fileName}>{file.fileName}</div>
        <div className="text-xs text-muted">{formatBytes(file.size)}</div>
        {file.status === 'sending' && (
          <div className="h-1 rounded-full bg-bg overflow-hidden mt-1">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="text-xs shrink-0">{statusNode}</div>
    </li>
  );
}

function AcceptSheet({
  files,
  remoteAlias,
  onAccept,
  onDecline,
}: {
  files: FileDto[];
  remoteAlias: string | null;
  onAccept: (ids: string[]) => void;
  onDecline: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(files.map((f) => f.id)));
  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const totalSize = files.filter((f) => selected.has(f.id)).reduce((s, f) => s + f.size, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink/40 backdrop-blur-sm animate-fade-in">
      <div className="card w-full md:w-[min(480px,92vw)] rounded-b-none md:rounded-lg animate-slide-up">
        <div className="px-5 py-4 border-b border-border/60">
          <h3 className="font-bold text-ink">接收文件请求</h3>
          <p className="text-xs text-muted mt-0.5">
            来自 <span className="text-ink">{remoteAlias ?? '未知设备'}</span>，共 {files.length} 个文件
          </p>
        </div>
        <ul className="max-h-64 overflow-y-auto divide-y divide-border/40">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 px-5 py-2.5">
              <input
                type="checkbox"
                checked={selected.has(f.id)}
                onChange={() => toggle(f.id)}
                className="w-4 h-4 accent-primary"
              />
              <span className="text-xl shrink-0">{fileEmoji(f.fileType)}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink truncate" title={f.fileName}>{f.fileName}</div>
                <div className="text-xs text-muted">{formatBytes(f.size)}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-border/60 flex items-center justify-between gap-2">
          <span className="text-xs text-muted">已选 {selected.size} 项 · {formatBytes(totalSize)}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onDecline} className="btn-ghost">拒绝</button>
            <button
              type="button"
              onClick={() => onAccept(Array.from(selected))}
              disabled={selected.size === 0}
              className="btn-primary"
            >
              接收
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function fileEmoji(fileType: FileDto['fileType']): string {
  switch (fileType) {
    case 'image':
      return '🖼️';
    case 'video':
      return '🎬';
    case 'audio':
      return '🎵';
    case 'text':
      return '📝';
    case 'application':
      return '📕';
    default:
      return '📄';
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
